import type { VideoJsPlayer } from 'video.js';

import { useCallback, useMemo, useRef } from 'react';

import { useLogger } from '../../../contexts/logger-context';
import { ErrorCode, PlayerError } from '../player-errors';

const isPlayerError = (err: unknown): err is PlayerError => {
  return err instanceof PlayerError;
};
const isPlayNotAllowedError = (err: unknown): err is PlayerError => {
  return isPlayerError(err) && err.code === ErrorCode.PLAYER_ERR_PLAY_NOT_ALLOWED;
};
const getPlayerErrorMessage = (err: unknown): string => {
  return isPlayerError(err) ? err.message : `${err}`;
};

export function usePlayHandler(player: VideoJsPlayer): {
  play: () => Promise<void>;
  autoplay: () => Promise<void>;
  autoplayMuted: () => Promise<void>;
} {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'PlayHandler' }), [mainLogger]);
  const playerRef = useRef(player);
  playerRef.current = player;

  const play = useCallback(async () => {
    logger.info('Try to play...');
    const player = playerRef.current;

    try {
      await player.play();
    } catch (err) {
      if (err instanceof Error && err.name === 'NotAllowedError') {
        throw new PlayerError(ErrorCode.PLAYER_ERR_PLAY_NOT_ALLOWED, err.message);
      } else if (err instanceof Error && err.name === 'AbortError') {
        // Play was interrupted by pause() - this is not an error, just ignore it
        return;
      } else {
        throw new PlayerError(ErrorCode.PLAYER_ERR_PLAY_NOT_POSSIBLE, `${err instanceof Error ? err.message : err}`);
      }
    }
  }, [logger]);

  const playMuted = useCallback(async () => {
    logger.info('Try to play muted...');

    const player = playerRef.current;
    const prevMutedState = player.muted();
    player.muted(true);

    try {
      await play();
    } catch (err) {
      player.muted(prevMutedState);

      throw err;
    }
  }, [play, logger]);

  /**
   * Tries to autoplay the video without checking the autoplay policy.
   *
   * This is a fallback for browsers that do not support the autoplay policy check.
   */
  const autoplayFallback = useCallback(async () => {
    logger.info('Autoplay policy unknown, attempting autoplay fallback...');

    try {
      await play();
    } catch (err) {
      if (isPlayNotAllowedError(err)) {
        try {
          await playMuted();
        } catch (err) {
          if (isPlayNotAllowedError(err)) {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_ALLOWED, err.message);
          } else {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_POSSIBLE, getPlayerErrorMessage(err));
          }
        }
      } else {
        throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_POSSIBLE, getPlayerErrorMessage(err));
      }
    }
  }, [logger, play, playMuted]);

  const autoplay = useCallback(async () => {
    logger.info('Try autoplay...');

    // Firefox supports autoplay policy check
    const autoplayPolicy = getAutoplayPolicy();

    switch (autoplayPolicy) {
      case 'allowed':
        try {
          await play();
        } catch (err) {
          if (isPlayNotAllowedError(err)) {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_ALLOWED, err.message);
          } else {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_POSSIBLE, getPlayerErrorMessage(err));
          }
        }
        break;
      case 'allowed-muted':
        try {
          await playMuted();
        } catch (err) {
          if (isPlayNotAllowedError(err)) {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_ALLOWED, err.message);
          } else {
            throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_MUTED_NOT_POSSIBLE, getPlayerErrorMessage(err));
          }
        }
        break;
      case 'disallowed':
        throw new PlayerError(ErrorCode.PLAYER_ERR_AUTOPLAY_NOT_ALLOWED, "Browser policy doesn't allow autoplay.");
      default:
        await autoplayFallback();
        break;
    }
  }, [logger, play, playMuted, autoplayFallback]);

  const autoplayMuted = useCallback(async () => {
    logger.info('Try autoplay muted...');

    const player = playerRef.current;

    const prevMutedState = player.muted();
    player.muted(true);

    try {
      await autoplay();
    } catch (err) {
      player.muted(prevMutedState);

      throw err;
    }
  }, [logger, autoplay]);

  return { play, autoplay, autoplayMuted };
}

type AutoplayPolicy = 'allowed' | 'allowed-muted' | 'disallowed';

function getAutoplayPolicy(): AutoplayPolicy | undefined {
  // only firefox supports autoplay policy check
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/getAutoplayPolicy

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  const navigator = window.navigator as any;
  return navigator.getAutoplayPolicy ? navigator.getAutoplayPolicy('mediaelement') : undefined;
}
